// src/components/onboarding/WelcomeScreen.js
import React, { useState } from 'react';
import { Sparkles } from 'lucide-react';
import PhotoUpload from '../common/PhotoUpload';

const WelcomeScreen = ({ initialData, onContinue }) => {
  const [displayName, setDisplayName] = useState(initialData.displayName || '');
  const [profilePhoto, setProfilePhoto] = useState(initialData.profilePhoto || null);
  const [profilePhotoPath, setProfilePhotoPath] = useState(initialData.profilePhotoPath || null);
  const [whyThisWork, setWhyThisWork] = useState(initialData.whyThisWork || '');

  const handleContinue = () => {
    if (!displayName.trim()) return;

    onContinue({
      displayName: displayName.trim(),
      profilePhoto,
      profilePhotoPath,
      whyThisWork: whyThisWork.trim()
    });
  };

  const canContinue = displayName.trim().length > 0;

  return (
    <div className="space-y-6">
      {/* Headline */}
      <div className="text-center mb-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-3 flex items-center justify-center gap-2">
          Welcome to Divine Frameworks
          <Sparkles className="w-6 h-6 text-purple-600" />
        </h1>
        <p className="text-sm text-gray-600 max-w-md mx-auto">
          You're taking a meaningful step. Let's set up the basics so your dashboard feels like yours.
        </p>
      </div>

      {/* Display Name Field */}
      <div>
        <label className="block text-sm font-medium text-gray-900 mb-2">
          What should we call you? <span className="text-red-500" aria-label="required">*</span>
        </label>
        <input
          type="text"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder="Your name or business name"
          className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          autoFocus
        />
        <p className="mt-2 text-xs text-gray-600">
          This is how clients will see you. You can always change this later in Settings.
        </p>
      </div>

      {/* Photo Upload */}
      <div>
        <label className="block text-sm font-medium text-gray-900 mb-2">
          Add a photo <span className="text-xs font-normal text-gray-500">(optional)</span>
        </label>
        <PhotoUpload
          currentPhoto={profilePhoto}
          onPhotoChange={(url, path) => { setProfilePhoto(url); setProfilePhotoPath(path); }}
        />
        <p className="mt-2 text-xs text-gray-600">
          You can always add this later.
        </p>
      </div>

      {/* Reflection Field */}
      <div>
        <label className="block text-sm font-medium text-gray-900 mb-2">
          What draws you to this work?
        </label>
        <textarea
          value={whyThisWork}
          onChange={(e) => setWhyThisWork(e.target.value)}
          placeholder="A few words about what drives you..."
          rows={3}
          className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
        />
        <p className="mt-2 text-xs text-gray-600">
          Just for you — we'll remind you when you need it most.
        </p>
      </div>

      {/* Continue Button */}
      <div className="pt-4">
        <button
          onClick={handleContinue}
          disabled={!canContinue}
          className={`w-full px-5 py-3 text-sm font-semibold rounded-lg transition-all ${
            canContinue
              ? 'bg-purple-600 text-white hover:bg-purple-700 shadow-md hover:shadow-lg'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
          }`}
        >
          Continue →
        </button>
      </div>
    </div>
  );
};

export default WelcomeScreen;
